import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScanLine, Keyboard, CameraOff } from 'lucide-react';

const REGION_ID = 'barcode-scanner-region';

// Camera-based scanner for QR + 1D barcodes (Code128, EAN, etc.) with a manual fallback.
export const BarcodeScanner = ({ onScan, onClose, title = 'Escanear código', description = 'Apunta la cámara al código de barras o QR del producto.' }) => {
  const [error, setError] = useState('');
  const [manual, setManual] = useState('');
  const scannerRef = useRef(null);
  const stoppingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        const scanner = new Html5Qrcode(REGION_ID, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 260, height: 160 } },
          (decoded) => { if (!cancelled) handleResult(decoded); },
          () => {},
        );
      } catch (e) {
        if (!cancelled) setError('No pudimos acceder a la cámara. Puedes ingresar el código manualmente.');
      }
    };
    start();
    return () => { cancelled = true; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stop = async () => {
    const scanner = scannerRef.current;
    if (!scanner || stoppingRef.current) return;
    stoppingRef.current = true;
    try { if (scanner.isScanning) await scanner.stop(); scanner.clear(); } catch { /* noop */ }
  };

  const handleResult = async (code) => {
    await stop();
    onScan((code || '').trim());
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) { stop(); onClose(); } }}>
      <DialogContent className="seller-dialog max-w-md" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScanLine className="h-5 w-5" /> {title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {error ? (
          <Alert data-testid="barcode-scanner-error"><CameraOff className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>
        ) : (
          <div id={REGION_ID} data-testid="barcode-scanner-region" className="overflow-hidden rounded-lg border border-border bg-black/5 min-h-52" />
        )}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="seller-help flex items-center gap-1 mb-1" htmlFor="barcode-manual"><Keyboard className="h-3.5 w-3.5" /> Ingreso manual</label>
            <Input id="barcode-manual" data-testid="barcode-manual-input" placeholder="Escribe o pega el código" value={manual} onChange={(e) => setManual(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && manual.trim()) handleResult(manual); }} />
          </div>
          <Button data-testid="barcode-manual-submit" disabled={!manual.trim()} onClick={() => handleResult(manual)}>Usar</Button>
        </div>
        <DialogFooter>
          <Button variant="outline" data-testid="barcode-scanner-cancel" onClick={() => { stop(); onClose(); }}>Cancelar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
